import React from "react";
import { Box, Chip, MenuItem, Tooltip, Typography } from "@mui/material";
import { SignalMetadata } from "../../../../types/recommendationEngine";

type Props = {
  signal: SignalMetadata;
  value: string;
  clamp01: boolean;
  showEndpointChips?: boolean;
};

const formatRange = (range: SignalMetadata["valueRange"]) => {
  if (range.max === null) {
    return `${range.min}+ unbounded`;
  }
  if (range.min === 0 && range.max === 1) {
    return "0–1 normalized";
  }
  return `${range.min}–${range.max}`;
};

const SignalDetailMenuItem = React.forwardRef<HTMLLIElement, Props>(
  ({ signal, value, clamp01, showEndpointChips = false, ...rest }, ref) => {
    const isRaw = signal.isRaw;
    const secondaryLine = signal.summary || signal.description;
    return (
      <MenuItem ref={ref} value={value} {...rest} sx={{ alignItems: "flex-start", py: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {signal.label}
            </Typography>
            <Chip
              size="small"
              label={isRaw ? "Raw count" : formatRange(signal.valueRange)}
              color={isRaw && clamp01 ? "warning" : "default"}
              variant="outlined"
              sx={{ height: 18, fontSize: 11 }}
            />
            <Chip
              size="small"
              label={signal.group}
              variant="outlined"
              sx={{ height: 18, fontSize: 11 }}
            />
            {signal.formulaHint && (
              <Tooltip title={signal.formulaHint} placement="top" arrow>
                <Chip
                  size="small"
                  label="Formula tip"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 11, cursor: "help" }}
                />
              </Tooltip>
            )}
          </Box>
          {secondaryLine && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "normal", lineHeight: 1.3 }}
            >
              {secondaryLine}
            </Typography>
          )}
          {signal.howItIsCalculated &&
            signal.howItIsCalculated !== signal.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                  fontStyle: "italic",
                }}
              >
                How it is calculated: {signal.howItIsCalculated}
              </Typography>
            )}
          {(signal.dataUsed?.length ?? 0) > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "normal", lineHeight: 1.3 }}
            >
              Data used: {(signal.dataUsed ?? []).join(", ")}
            </Typography>
          )}
          {(signal.relatedCandidateSources?.length ?? 0) > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.25,
                alignItems: "center",
                mt: 0.25,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                From sources:
              </Typography>
              {(signal.relatedCandidateSources ?? []).map((source) => (
                <Chip
                  key={source}
                  size="small"
                  label={source}
                  variant="outlined"
                  sx={{ height: 16, fontSize: 10 }}
                />
              ))}
            </Box>
          )}
          {showEndpointChips && signal.applicableEndpoints?.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, mt: 0.25 }}>
              {signal.applicableEndpoints.map((endpoint) => (
                <Chip
                  key={endpoint}
                  size="small"
                  label={endpoint}
                  variant="outlined"
                  sx={{ height: 16, fontSize: 10 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </MenuItem>
    );
  },
);

SignalDetailMenuItem.displayName = "SignalDetailMenuItem";

export default SignalDetailMenuItem;
